import { useEffect, useState } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import { z } from 'zod';

import { AppointmentName, groupedApptTypesByReasonForVisit } from '../components/ReasonForVisit/ReasonForVisit.helpers';

import { dataLayerConfig } from '@shared-components-web';
import { radioTileSchema, SchemaBuilder, useHookForm } from '@shared-forms-web';
import { getLogger } from '@src/lib/logging/logger';
import {
	useAppointmentSelectionStore,
	useClientInformationStore,
	useFacilityStore,
	usePatientInformationStore,
	useReasonForVisitStore,
	useRouteHandlerStore,
	useTimeSlotStore,
} from '@src/store';
import useGlobalUXStore from '@src/store/modules/global-ux.store';
import { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';
import { scheduleTypeOptions } from '@src/utils/determine-schedule-type';
import { stringifyAppointmentTypes } from '@src/utils/stringify-appointment-types';

export function useReasonForVisitForm({ setLoading }: { setLoading: (loading: boolean) => void }) {
	const [revealSicknessOptions, setRevealSicknessOptions] = useState(false);
	const [helpModalOpen, setHelpModalOpen] = useState(false);
	const [rfvChangeModalOpen, setRfvChangeModalOpen] = useState(false);
	const logger = getLogger('client');

	const { advanceToNextRoute, navToPriorRoute } = useRouteHandlerStore((store) => store);
	const { clientId } = useClientInformationStore((store) => store);
	const { notes, setNotes, notesReasonForVisit, setNotesReasonForVisit } = usePatientInformationStore(
		(store) => store
	);
	const {
		mainReasonForVisit,
		setMainReasonForVisit,
		setAppointmentTypes,
		setReasonsForVisitList,
		reasonsForVisitList,
	} = useReasonForVisitStore((store) => store);
	const { allAppointmentTypes, facility, scheduleType } = useFacilityStore((store) => store);
	const { setGlobalErrorDialogOpen } = useGlobalUXStore((store) => store);
	const { fromPAO, setFromPAO } = useAppointmentSelectionStore((store) => store);
	const { setTimeSlot, setTimeSlotFacility } = useTimeSlotStore((store) => store);

	const schema = SchemaBuilder([
		{ name: 'visitReason', validation: radioTileSchema.options.default },
		{
			name: 'specificReasonForVisit',
			validation: z
				.string()
				.array()
				.min(revealSicknessOptions ? 1 : 0, 'You must select at least one reason') as any,
		},
	]);

	let visitReason = '';

	if (mainReasonForVisit === ReasonForVisitType.SickNeeds) {
		visitReason = 'sick';
	}

	if (mainReasonForVisit === ReasonForVisitType.RoutineCheckUp) {
		visitReason = 'checkup';
	}

	const form = useHookForm({
		schema,
		defaultValues: {
			visitReason,
			specificReasonForVisit: reasonsForVisitList,
		},
	});

	const visitReasonFieldValue = useWatch({ name: 'visitReason', control: form.control as Control });

	useEffect(() => {
		setRevealSicknessOptions(visitReasonFieldValue === 'sick');
		if (
			fromPAO &&
			mainReasonForVisit === ReasonForVisitType.SickNeeds &&
			visitReasonFieldValue &&
			visitReasonFieldValue !== 'sick' &&
			scheduleType === scheduleTypeOptions.closed
		) {
			openRFVChangeModal();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visitReasonFieldValue]);

	const handleOpenModal = () => setHelpModalOpen(true);
	const handleCloseModal = () => setHelpModalOpen(false);
	const openRFVChangeModal = () => setRfvChangeModalOpen(true);
	const closeRFVChangeModal = () => setRfvChangeModalOpen(false);
	const handleRFVChangeCancel = () => {
		form.setValue('visitReason', 'sick');
		closeRFVChangeModal();
	};

	const handleRFVChangeProceed = () => {
		setTimeSlot(null);
		setTimeSlotFacility(null);
		setFromPAO(false);
		closeRFVChangeModal();
		// Update mainReasonForVisit to match the user's new selection so the
		// form doesn't revert to the previous PAO reason on re-render.
		if (visitReasonFieldValue === 'checkup') {
			setMainReasonForVisit(ReasonForVisitType.RoutineCheckUp);
		}
	};

	const logReasonForVisitSelectingError = (reason: string) => {
		dataLayerConfig({
			event: 'error',
			step_name: 'reason_for_visit_error',
			error_details: `no matched appointment types found for ${reason}`,
			error_text: 'There has been an error.',
			error_type: 'reason_for_visit',
			error_status: 'no',
			...(clientId && { client_id: clientId }),
		});
		logger.info({
			name: '',
			category: 'Client Error - No Matched Appointment Types',
			value: `client: ${clientId} no matched appointment types found for ${reason} @: ${facility?.displayName} (${
				facility?.id
			}), all appointment types - ${stringifyAppointmentTypes(allAppointmentTypes)}`,
		});
	};

	function handleReasonForVisit({
		reasonType,
		visitReason,
		specificReasonForVisit,
	}: {
		reasonType: ReasonForVisitType;
		visitReason: string;
		specificReasonForVisit: string[];
	}) {
		const appointmentTypeMatches = allAppointmentTypes?.filter((appointmentType) =>
			groupedApptTypesByReasonForVisit[reasonType].includes(appointmentType.name as AppointmentName)
		);
		if (!appointmentTypeMatches || appointmentTypeMatches.length === 0) {
			logReasonForVisitSelectingError(visitReason);
			if (setLoading) setLoading(false);
			setGlobalErrorDialogOpen(true);
			return false;
		}
		setAppointmentTypes(appointmentTypeMatches);
		setMainReasonForVisit(reasonType);
		setReasonsForVisitList(specificReasonForVisit);
		return true;
	}

	return {
		form,
		revealSicknessOptions,
		helpModalOpen,
		handleOpenModal,
		handleCloseModal,
		handleReasonForVisit,
		facility,
		scheduleType,
		clientId,
		mainReasonForVisit,
		reasonsForVisitList,
		notes,
		setNotes,
		notesReasonForVisit,
		setNotesReasonForVisit,
		advanceToNextRoute,
		navToPriorRoute,
		fromPAO,
		setFromPAO,
		setTimeSlot,
		setTimeSlotFacility,
		rfvChangeModalOpen,
		closeRFVChangeModal,
		handleRFVChangeCancel,
		handleRFVChangeProceed,
		visitReasonFieldValue,
	};
}
